import { getBarStartTime } from '../TradingViewDatafeedService/util.js';
import type {
    FetchHistoryRequest,
    LightweightChartBarDataService,
    OHLCVBar,
    SubscribeRealtimeRequest,
} from './index.js';
import { Instant, ZoneId } from '@js-joda/core';
import '@js-joda/timezone';
import { inject, injectable } from 'src/features/ioc/index.js';
import { getBardexToken, getBars } from 'src/lib/bardex.js';
import { createTradingViewTable } from 'src/lib/table.js';
import type { ReactBindings } from 'src/types/bindings.js';

@injectable()
export class LightweightChartBarDataServiceImpl implements LightweightChartBarDataService {
    readonly #log: ReturnType<ReactBindings['Logger']['getSubLogger']>;
    readonly #tableModelFactory: ReactBindings['TableModelFactory'];
    readonly #activeSubscriptions = new Set<DisposableStack>();

    constructor(
        @inject('Logger') logger: ReactBindings['Logger'],
        @inject('TableModelFactory') tableModelFactory: ReactBindings['TableModelFactory'],
    ) {
        this.#log = logger.getSubLogger({ name: 'LightweightChartBarDataService' });
        this.#tableModelFactory = tableModelFactory;
        if (TRACE) {
            this.#log.trace('LightweightChartBarDataService initialized');
        }
    }

    async fetchHistory(req: FetchHistoryRequest): Promise<readonly OHLCVBar[]> {
        if (!req.ticker) {
            throw new Error('fetchHistory: missing ticker');
        }
        const log = this.#log.getSubLogger({ method: 'fetchHistory' });
        if (TRACE) {
            log.trace('Fetching bars', {
                ticker: req.ticker,
                barLength: req.barLengthBardex,
                from: req.from,
                to: req.to,
            });
        }

        const fromInstant = Instant.ofEpochSecond(req.from);
        const toInstant = Instant.ofEpochSecond(req.to);

        const { accessCookie } = await getBardexToken({
            symbol: [req.ticker],
            start: fromInstant,
            end: toInstant,
        });

        const request = {
            symbols: [req.ticker],
            timeframe: { start: fromInstant, end: toInstant },
            barLength: req.barLengthBardex,
            bars: ['BarOpen', 'BarHigh', 'BarLow', 'BarClose', 'BarVolume'],
            countBack: req.countBack,
            sessionId: req.sessionId,
            sessionWin: req.sessionWindow,
            sessionTz: req.timezone,
        };

        const bars = await getBars(request, accessCookie)
            .map(
                (point): OHLCVBar => ({
                    time: point.unixTime,
                    open: point.bars['BarOpen'],
                    high: point.bars['BarHigh'],
                    low: point.bars['BarLow'],
                    close: point.bars['BarClose'],
                    volume: point.bars['BarVolume'],
                }),
            )
            .toArray();

        return bars;
    }

    subscribeRealtime(req: SubscribeRealtimeRequest, onBar: (bar: OHLCVBar) => void): Disposable {
        if (!req.ticker) {
            throw new Error('subscribeRealtime: missing ticker');
        }
        const log = this.#log.getSubLogger({ method: 'subscribeRealtime' });
        if (TRACE) {
            log.trace('Subscribing to realtime bars', {
                ticker: req.ticker,
                barLength: req.barLength,
                extended: req.extended,
            });
        }

        const timezone = ZoneId.of(req.timezone);
        const stack = new DisposableStack();
        const tableModel = this.#tableModelFactory();
        stack.use(tableModel);

        // Reused mutable bar object so each realtime tick does not allocate.
        // The callback fires synchronously; downstream consumers must read
        // the fields before the next tick if they intend to retain them.
        const bar: OHLCVBar = { time: 0, open: 0, high: 0, low: 0, close: 0, volume: 0 };
        let lastTimestamp = 0;
        let lastAdjustedTimestamp = 0;
        let lastLogTime = 0;

        stack.use(
            tableModel.update$.subscribe((row) => {
                if (TRACE) {
                    const now = performance.now();
                    if (now - lastLogTime >= 10000) {
                        lastLogTime = now;
                        // eslint-disable-next-line @thinkalpha/eslint-rules/wrap-log-trace -- throttled inside an outer if(TRACE)
                        log.trace(`Realtime bar update for ${req.ticker}`, { ticker: req.ticker, row });
                    }
                }
                let timestamp = row.Timestamp;
                if (isNaN(timestamp)) {
                    if (TRACE) {
                        log.trace('Skipping NaN timestamp', { ticker: req.ticker, row });
                    }
                    return;
                }
                if (lastTimestamp === timestamp) {
                    timestamp = lastAdjustedTimestamp;
                } else {
                    lastTimestamp = timestamp;
                    timestamp = getBarStartTime(row.Timestamp, req.barLength, timezone, req.fullMarketSession);
                    lastAdjustedTimestamp = timestamp;
                }
                bar.time = timestamp;
                bar.open = row.Open;
                bar.high = row.High;
                bar.low = row.Low;
                bar.close = row.Close;
                bar.volume = row.Volume;
                onBar(bar);
            }),
        );

        // `tableModel.init` can throw synchronously (bad query options, IoC
        // resolution failure, etc.). If it does, `stack` is the only handle
        // to the partially-constructed resources and would leak — dispose it
        // before rethrowing so the caller's `using` block can't see a
        // half-initialised subscription.
        try {
            tableModel.init({
                queryOptions: {
                    queryKey: ['trading-view-bars', req.ticker, req.barLength],
                    queryFn: async () =>
                        createTradingViewTable({
                            barLength: req.barLength,
                            symbols: [req.ticker],
                            extended: req.extended,
                        }),
                    staleTime: 0,
                },
                initBounds: { firstRow: 0, windowSize: 1 },
                devtoolsName: 'LightweightChartBarDataService',
            });
        } catch (error) {
            stack[Symbol.dispose]();
            throw error;
        }

        this.#activeSubscriptions.add(stack);

        return {
            [Symbol.dispose]: () => {
                if (TRACE) {
                    log.trace('Unsubscribing realtime bars', { ticker: req.ticker });
                }
                this.#activeSubscriptions.delete(stack);
                stack[Symbol.dispose]();
            },
        };
    }

    [Symbol.dispose](): void {
        if (TRACE) {
            this.#log.trace('LightweightChartBarDataService disposing', {
                activeSubscriptions: this.#activeSubscriptions.size,
            });
        }
        for (const stack of this.#activeSubscriptions) {
            stack[Symbol.dispose]();
        }
        this.#activeSubscriptions.clear();
    }
}
